'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import CountUp from 'react-countup';
import useGeneralSounds from '@/hooks/useGeneralSounds';
import { summarizeSCBalances } from '@/libs/summarizeSCBalances';
import { useWalletMeQuery } from '@/queries/wallet/wallet.queries';

type BalanceType = 'GC' | 'SC';

interface Props {
  type: BalanceType;
}

function AnimatedBalance({ type = 'SC' }: Props) {
  const { data: coins, isLoading } = useWalletMeQuery();
  const [soundsEnabled] = useGeneralSounds();
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!soundsEnabled) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      return;
    }

    if (!audioRef.current) {
      const audio = new Audio('/sounds/coin-drop.mp3');
      audio.preload = 'auto';
      audioRef.current = audio;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [soundsEnabled]);

  const chooseCoins = useMemo(() => {
    if (coins) {
      return type === 'GC'
        ? Number(coins.goldCoin)
        : summarizeSCBalances(Number(coins.sweepCoin), Number(coins.bonusSweepCoin));
    }

    return 0;
  }, [coins, type]);
  const isInitializedPerTypeRef = useRef<Record<BalanceType, boolean>>({ SC: false, GC: false });

  useEffect(() => {
    if (isLoading || !coins) {
      return;
    }

    if (isInitializedPerTypeRef.current[type]) {
      return;
    }

    const currentValue = chooseCoins;
    const savedValue = localStorage.getItem(type);

    if (!savedValue) {
      localStorage.setItem(type, String(currentValue));
      setStart(currentValue / 100);
      setEnd(currentValue / 100);
    } else {
      const savedNumber = Number(savedValue);
      setStart(savedNumber / 100);
      setEnd(savedNumber / 100);
    }

    isInitializedPerTypeRef.current[type] = true;
  }, [coins, isLoading, chooseCoins, type]);

  useEffect(() => {
    if (isLoading || !isInitializedPerTypeRef.current[type]) {
      return;
    }

    const currentValue = chooseCoins;
    const savedValue = localStorage.getItem(type);
    const previousValue = savedValue ? Number(savedValue) : currentValue;

    if (currentValue > previousValue) {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        void audio.play();
      }
    }

    if (currentValue !== previousValue) {
      setStart(previousValue / 100);
      setEnd(currentValue / 100);

      localStorage.setItem(type, String(currentValue));
    } else {
      setStart(currentValue / 100);
      setEnd(currentValue / 100);
    }
  }, [chooseCoins, isLoading, type, coins]);

  return (
    <CountUp
      className="3xl:text-[1rem]"
      start={start}
      end={end}
      duration={1.5}
      decimals={2}
    />
  );
}

export default AnimatedBalance;
